import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArticleGenerationRow,
  ArticlePart,
  GenerationSection,
} from '../../types/articleGeneration';
import { articleGenerationService, ReviseSpanError } from '../../services/articleGenerationService';
import { useNotification } from '../../contexts/NotificationContext';
import { extractQuoteTexts, runQualityChecks } from './qualityChecks';
import { buildMarkdown } from './markdown';
import { locateSpan } from './spanLocator';
import QualityChecklist from './QualityChecklist';
import SpanNotePopover from './SpanNotePopover';

interface Props {
  run: ArticleGenerationRow;
  onNoteSaved: (patch: Partial<ArticleGenerationRow>) => void;
  onRegeneratePart: (part: ArticlePart, note: string) => void;
  onContinue: () => void;
  isGenerating: boolean;
}

/** A passage the user highlighted inside one section, with where to show the controls. */
interface Highlight {
  sectionIndex: number;
  text: string;
  x: number;
  y: number;
}

const ERROR_KEYS: Record<string, string> = {
  span_mismatch: 'article.span_note_stale',
  already_applied: 'article.span_note_already_applied',
  revision_rejected: 'article.span_note_rejected',
};

const ArticleResult: React.FC<Props> = ({
  run,
  onNoteSaved,
  onRegeneratePart,
  onContinue,
  isGenerating,
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const sections = run.sections ?? [];
  const h1 = run.structure?.h1 ?? '';

  // Highlight → note → one rewrite of that passage (spec 2026-09-10).
  const [highlight, setHighlight] = useState<Highlight | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [revising, setRevising] = useState(false);
  const [justRevised, setJustRevised] = useState<number | null>(null);

  const markdown = useMemo(() => buildMarkdown(h1, sections), [h1, sections]);
  const quality = useMemo(
    () => runQualityChecks(markdown, extractQuoteTexts(run.inputs.quotes)),
    [markdown, run.inputs.quotes]
  );
  const wordCount = markdown.trim().split(/\s+/).filter(Boolean).length;
  const resumable = ['incomplete', 'cancelled', 'error', 'needs_review'].includes(run.status);

  const saveNote = async (section: GenerationSection, note: string) => {
    const next = sections.map((s) => (s.index === section.index ? { ...s, note } : s));
    await articleGenerationService.updateSections(run.id, next);
    onNoteSaved({ sections: next });
  };

  const closeNote = () => {
    setNoteOpen(false);
    setHighlight(null);
  };

  // Runs on mouse-up inside one section's rendered body: a non-empty selection
  // that stays inside that section becomes a highlight with a floating button.
  const captureHighlight =
    (section: GenerationSection) => (event: React.MouseEvent<HTMLDivElement>) => {
      if (revising || isGenerating || section.status !== 'done') return;
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        if (!noteOpen) setHighlight(null);
        return;
      }
      const text = selection.toString().trim();
      if (!text) return;
      const range = selection.getRangeAt(0);
      if (!event.currentTarget.contains(range.commonAncestorContainer)) {
        showNotification(t('article.span_note_not_found'), 'info');
        return;
      }
      // Place the controls under the selection, kept inside the viewport.
      const rect = range.getBoundingClientRect();
      const x = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - 440));
      const y = Math.min(Math.max(8, rect.bottom + 8), Math.max(8, window.innerHeight - 56));
      setHighlight({ sectionIndex: section.index, text, x, y });
      setNoteOpen(false);
    };

  const submitNote = async (note: string) => {
    if (!highlight) return;
    const section = sections.find((s) => s.index === highlight.sectionIndex);
    if (!section) return;
    const span = locateSpan(section.body, highlight.text);
    if (!span) {
      showNotification(t('article.span_note_not_found'), 'error');
      return;
    }
    setRevising(true);
    try {
      const result = await articleGenerationService.reviseSpan({
        action: 'revise_span',
        generationId: run.id,
        companyId: run.company_id,
        sectionIndex: section.index,
        start: span.start,
        end: span.end,
        selectedText: highlight.text,
        note,
      });
      const next = sections.map((s) =>
        s.index === section.index ? { ...s, body: result.body } : s
      );
      onNoteSaved({ sections: next });
      showNotification(t('article.span_note_done'), 'success');
      setJustRevised(section.index);
      setTimeout(() => setJustRevised(null), 2500);
      window.getSelection()?.removeAllRanges?.();
      closeNote();
    } catch (error) {
      const code = error instanceof ReviseSpanError ? error.code : 'unknown';
      const key = ERROR_KEYS[code];
      showNotification(
        key
          ? t(key)
          : t('article.span_note_failed', {
              message: error instanceof Error ? error.message : String(error),
            }),
        'error'
      );
      // The passage is stale or the note is spent: the highlight is no longer usable.
      if (code !== 'unknown') closeNote();
    } finally {
      setRevising(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-gray-600">
          {wordCount} {t('article.words')} · {markdown.length} {t('article.characters')}
        </div>
        <div className="flex gap-2">
          {resumable && (
            <button
              type="button"
              onClick={onContinue}
              disabled={isGenerating}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
            >
              {t('article.continue_generation')}
            </button>
          )}
        </div>
      </div>
      <QualityChecklist result={quality} />
      <p className="text-xs text-gray-500">{t('article.span_note_hint')}</p>
      {[...sections]
        .sort((a, b) => a.index - b.index)
        .map((section) => (
          <div
            key={section.index}
            className={`border rounded-lg p-4 bg-white space-y-2 transition-shadow ${
              justRevised === section.index ? 'ring-2 ring-green-400' : ''
            }`}
          >
            {section.heading && <h3 className="font-semibold text-lg">{section.heading}</h3>}
            {section.status === 'failed' && (
              <div className="text-sm text-red-600">{t('article.section_failed')}</div>
            )}
            <div
              data-testid={`section-body-${section.index}`}
              onMouseUp={captureHighlight(section)}
              className="prose max-w-none text-sm"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.body}</ReactMarkdown>
            </div>
            <div className="flex gap-2 items-center">
              <input
                defaultValue={section.note}
                placeholder={t('article.note_placeholder')}
                onBlur={(e) => {
                  if (e.target.value !== section.note) saveNote(section, e.target.value);
                }}
                className="flex-1 p-2 border rounded-md text-sm"
              />
              <button
                type="button"
                disabled={isGenerating}
                onClick={() =>
                  onRegeneratePart({ kind: 'section', index: section.index }, section.note)
                }
                className="px-3 py-1.5 border border-blue-600 text-blue-600 rounded-md text-sm disabled:opacity-50"
              >
                {t('article.regenerate_part')}
              </button>
            </div>
          </div>
        ))}

      {highlight && !noteOpen && (
        <button
          type="button"
          style={{ position: 'fixed', left: `${highlight.x}px`, top: `${highlight.y}px` }}
          // Keep the text selection alive while the button is pressed.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setNoteOpen(true)}
          className="z-30 px-2.5 py-1 text-xs font-medium bg-gray-900 text-white rounded-md shadow-lg hover:bg-gray-700"
        >
          {t('article.span_note_button')}
        </button>
      )}
      {highlight && noteOpen && (
        <SpanNotePopover
          selectedText={highlight.text}
          anchor={{ x: highlight.x, y: highlight.y }}
          busy={revising}
          onSubmit={submitNote}
          onClose={closeNote}
        />
      )}
    </div>
  );
};

export default ArticleResult;
